import { useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, Button, Flex, Text, Box, Image, Checkbox, Spinner, Badge,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchCuratedPlaylists, type CuratedPlaylistListItemDto } from '../../../api/libraryApi';

interface PlaylistPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** ids already in the target list — pre-checked and effectively "add" is a full replace upstream. */
  selectedIds: string[];
  /** Called with the new full set of selected ids when the user confirms. */
  onConfirm: (playlistIds: string[]) => void;
}

/**
 * Picks published library playlists to add to a collection or related override.
 * Reuses the existing admin curated-playlists list endpoint (library playlists);
 * only published ones are selectable — the backend rejects the rest with 422, so we
 * filter to published here to give immediate feedback.
 */
export function PlaylistPickerModal({ open, onClose, selectedIds, onConfirm }: PlaylistPickerModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(selectedIds));

  const { data: playlists, isLoading } = useQuery({
    queryKey: ['admin-curated-playlists-picker'],
    queryFn: fetchCuratedPlaylists,
    enabled: open,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (playlists ?? []).filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [playlists, search]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleConfirm() {
    onConfirm([...selected]);
    onClose();
  }

  return (
    <Modal isOpen={open} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="playlist-picker-modal">
        <ModalHeader>{t('admin.playlistCollections.picker.title', 'Add playlists')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search', 'Search')}
            mb={4}
            data-testid="playlist-picker-search"
          />
          {isLoading ? (
            <Flex justify="center" p={6}><Spinner /></Flex>
          ) : filtered.length === 0 ? (
            <Text color="gray.500" fontSize="sm" data-testid="playlist-picker-empty">
              {t('admin.playlistCollections.picker.empty', 'No playlists found')}
            </Text>
          ) : (
            filtered.map((p: CuratedPlaylistListItemDto) => (
              <Flex
                key={p.id}
                align="center"
                gap={3}
                px={3}
                py={2}
                mb={1}
                borderRadius="10px"
                _hover={{ bg: 'gray.50' }}
                cursor="pointer"
                onClick={() => toggle(p.id)}
                data-testid={`playlist-picker-item-${p.id}`}
              >
                <Checkbox
                  isChecked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  pointerEvents="none"
                  data-testid={`playlist-picker-check-${p.id}`}
                />
                <Box w="36px" h="36px" flexShrink={0} borderRadius="8px" overflow="hidden" bg="gray.100">
                  {p.thumbnailUrl && (
                    <Image src={p.thumbnailUrl} alt={p.name} w="100%" h="100%" objectFit="cover" />
                  )}
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontWeight="500" fontSize="sm" noOfLines={1}>{p.name}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('admin.playlists.tracks', 'tracks')}: {p.trackCount}
                  </Text>
                </Box>
                {!p.isPublished && (
                  <Badge colorScheme="gray" fontSize="2xs">{t('admin.playlists.draft', 'Draft')}</Badge>
                )}
              </Flex>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} data-testid="playlist-picker-cancel">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button colorScheme="brand" onClick={handleConfirm} data-testid="playlist-picker-confirm">
            {t('admin.playlistCollections.picker.confirm', 'Add selected')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
